import { useMemo, useState } from 'react';
import { KeyEditorContext } from './KeyEditorContext';
import { IDataFrameSchema } from '@/lib/dataFrameTypes';
import { TCompareDefinition, ICompareSchemaField, TAssociation } from '@/lib/tabularCompareApi';
import isEqual from 'lodash-es/isEqual';
import useAuthorization from '@/hooks/useAuthorization';

import {
  initializeAssociations,
  compileCompareDefinition,
  getMergedFields,
  initializeSelections,
  getLeftFields,
  getRightFields,
  getUnLinkedFields,
} from '../CompareBuilder/lib';
import { IRepository } from '@/types';

const KeyEditorProvider = ({
  repository,
  compare: initialCompare,
  schemaLeft,
  schemaRight,
  requireFieldNameMatch,
  children,
}: {
  repository: IRepository;
  schemaLeft: IDataFrameSchema;
  schemaRight: IDataFrameSchema;
  requireFieldNameMatch?: boolean;
  compare: TCompareDefinition;
  children: React.ReactNode;
}) => {
  const [compare, setCompare] = useState(initialCompare);
  const { data_frames } = compare;

  const mergedFields = getMergedFields(schemaLeft, schemaRight);
  const initialSelectedFields = initializeSelections(mergedFields, compare);

  const [selectedFields, setSelectedFields] = useState<ICompareSchemaField[]>(initialSelectedFields);
  const { title, description } = compare;

  const initialAssociations = initializeAssociations(
    getLeftFields(schemaLeft, selectedFields),
    getRightFields(schemaRight, selectedFields),
    compare,
  );

  const [associations, setAssociations] = useState<TAssociation[]>(initialAssociations);

  const leftFields = useMemo(() => getLeftFields(schemaLeft, selectedFields), [schemaLeft, selectedFields]);
  const rightFields = useMemo(() => getRightFields(schemaRight, selectedFields), [schemaRight, selectedFields]);

  // TODO: Review when inpplementing handling of display fields
  const unLinkedFields = useMemo(
    () => getUnLinkedFields(leftFields, rightFields, associations),
    [leftFields, rightFields, associations],
  );

  const compiledDefinition = useMemo(
    () =>
      compileCompareDefinition(associations, unLinkedFields, data_frames.left, data_frames.right, title, description),
    [associations, unLinkedFields, data_frames, title, description],
  );

  const reset = () => {
    setCompare(initialCompare);
    setSelectedFields(initialSelectedFields);
    setAssociations(initialAssociations);
  };

  const toggleKeyField = (field: ICompareSchemaField) => {
    setAssociations(
      associations.map((a) =>
        a.left.name === field.name || a.right.name == field.name ? { ...a, isKey: !a.isKey, isTarget: false } : a,
      ),
    );
  };

  const toggleTargetField = (field: ICompareSchemaField) => {
    setAssociations(
      associations.map((a) =>
        a.left.name === field.name || a.right.name == field.name ? { ...a, isTarget: !a.isTarget, isKey: false } : a,
      ),
    );
  };

  const auth = useAuthorization();
  const canEdit = auth.forRepository(repository).canEdit();

  const isValid = useMemo(() => {
    const { key } = compiledDefinition.fields;
    return key.length > 0;
  }, [compiledDefinition]);

  const isDirty = useMemo(() => {
    return (
      !isEqual(compiledDefinition.fields.key, initialCompare.fields.key) ||
      !isEqual(compiledDefinition.fields.compare, initialCompare.fields.compare)
    );
  }, [compiledDefinition, initialCompare]);

  const errorMessages = useMemo(() => {
    const errors: string[] = [];
    if (selectedFields.length < 1) {
      return errors;
    }
    const { key } = compiledDefinition.fields;
    if (key.length === 0) {
      errors.push('You must select at least one key field');
    }
    return errors;
  }, [compiledDefinition, selectedFields]);

  return (
    <KeyEditorContext.Provider
      value={{
        compare,
        requireFieldNameMatch,
        mergedFields,
        associations,
        unLinkedFields,
        selectedFields,
        leftFields,
        rightFields,
        compiledDefinition,
        toggleKeyField,
        toggleTargetField,
        reset,
        isValid,
        isDirty,
        errorMessages,
        canEdit,
      }}
    >
      {children}
    </KeyEditorContext.Provider>
  );
};

export type TKeyEditorProvider = {
  compare: TCompareDefinition;
  requireFieldNameMatch?: boolean;
  associations: TAssociation[];
  mergedFields: ICompareSchemaField[];
  unLinkedFields: ICompareSchemaField[];
  selectedFields: ICompareSchemaField[];
  leftFields: ICompareSchemaField[];
  rightFields: ICompareSchemaField[];
  compiledDefinition: TCompareDefinition;
  reset: () => void;
  toggleKeyField: (field: ICompareSchemaField) => void;
  toggleTargetField: (field: ICompareSchemaField) => void;
  isValid: boolean;
  isDirty: boolean;
  errorMessages: string[];
  canEdit: boolean;
};

export default KeyEditorProvider;
